// Library — the public FREE shelf (/library).
//
// Every title here was published free by its author: open any one and read it in
// full, in the browser, signed in or not. No price, no checkout. This is a
// distinct surface from the paid Marketplace (a book is one or the other, never
// both).
//
// Structured as one page with four tabs — Reading Now, Book Store, Browse all,
// Collections — switched via `?tab=`.
//
//   Why `?tab=` and not nested routes: `App.tsx`'s `ScrollToTop` is
//   pathname-keyed (and its own comment cites My Books' `?tab=` as the case it
//   deliberately preserves), so `/library/store` would scroll-jump on every tab
//   click and on every Back. It also keeps `GuestOnlyRoute`'s post-login
//   `/library` default and Shell's nav active-matching working untouched.
//
// This page stays eagerly imported — it is where login lands — but the four tab
// compositions are lazy, so only the active one ships on first paint.
//
// DATA. Live, from four endpoints:
//
//   GET /library/tabs/reading-now   hero, rails, genre chips, totals   (public)
//   GET /library/tabs/store         promos, charts, super-groups        (public)
//   GET /library/browse             the filtered/sorted/paged shelf     (public)
//   GET /library/me/shelf           continue-reading + wishlist ids     (signed in)
//
// The tab payloads are deliberately anonymous so they stay cacheable; everything
// per-reader is the one `/me/shelf` call, merged into Reading Now here. Each tab
// still takes its payload as a PROP and never fetches, so the tab files know
// nothing about the network.
//
// Two things the shelf does not have yet, surfaced honestly rather than faked:
// curated Collections (no curation tables) and "Most read" (nothing counts reads).
// The server reports both — `totals.collections` and `charts.available_metrics` —
// so the UI degrades from data rather than from a hardcoded flag.
//
// `?state=loading|error|empty` still forces every section into that state, which
// is how the three are reviewed without waiting for a slow network.

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Shell } from "../components/Shell";
import { useAuth } from "../lib/AuthContext";
import type {
  BrowseSort,
  IllustratedFilter,
  LengthBand,
  LibraryTab,
  SectionState,
  ShelfBook,
  ShelfTotals,
} from "../lib/library-types";
import { fetchBrowse, fetchMyShelf, fetchReadingNow, fetchStore } from "../lib/library-api";
import { bookHref } from "../lib/libraryRoutes";
import { useAsync } from "../lib/useAsync";
import { LibraryTabBar } from "./library/LibraryTabBar";
import { ShelfSearchField } from "../components/library/ShelfSearchField";
import { DEFAULT_TAB, LIBRARY_TABS, tabFromParam, tabMeta } from "./library/tabs";
import type { BrowseFilterState } from "./library/BrowseAllTab";
import { RailSkeleton, BlockSkeleton } from "../components/library/skeletons";

const ReadingNowTab = lazy(() => import("./library/ReadingNowTab"));
const BookStoreTab = lazy(() => import("./library/BookStoreTab"));
const BrowseAllTab = lazy(() => import("./library/BrowseAllTab"));
const CollectionsTab = lazy(() => import("./library/CollectionsTab"));

/** Params that belong to one tab only, cleared when leaving it.
 *
 *  `q` is deliberately NOT here. The field is on every tab, so a query survives a
 *  tab change — the text stays in the box, and coming back to Browse all shows the
 *  same results rather than silently having thrown them away. `page` IS scoped:
 *  page 3 of one result set means nothing in another. */
const TAB_SCOPED_PARAMS = ["kind", "length", "plates", "sort", "page", "c"];

const SORTS: BrowseSort[] = ["newest", "oldest", "longest", "shortest", "most-read", "title"];
const BANDS: LengthBand[] = ["under-100", "100-250", "over-250"];

/** Matches `BROWSE_DEFAULT_LIMIT` in `app/api/library.py`. The client sends it
 *  explicitly so "Showing 19–36 of 164" is arithmetic on a number we chose, not on
 *  a server default we assumed. */
const PAGE_SIZE = 18;

/** Rows in the typeahead. The design's six. */
const SUGGEST_LIMIT = 6;

/** Keystroke-to-request delay. Long enough that typing a whole word is one request,
 *  short enough that the list feels attached to the keyboard. */
const SUGGEST_DEBOUNCE_MS = 180;

/** Until the first payload lands there are no real numbers, so the tab bar shows zeroes
 *  rather than inventing a shelf size. */
const EMPTY_TOTALS: ShelfTotals = {
  books: 0,
  kinds: 0,
  next_ordinal: 1,
  new_this_week: 0,
  collections: 0,
};

function isSectionState(value: string | null): value is SectionState {
  return value === "loading" || value === "error" || value === "empty" || value === "ready";
}

export default function Library() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, ready: authReady } = useAuth();
  const signedIn = Boolean(user);

  const tab = tabFromParam(params.get("tab"));
  const query = params.get("q") ?? "";
  // A junk `?page=` is a URL a reader may have edited or a stale bookmark: clamp to
  // the first page rather than sending NaN to the server.
  const page = Math.max(1, Number.parseInt(params.get("page") ?? "1", 10) || 1);
  const reviewOverride: SectionState | null = isSectionState(params.get("state"))
    ? (params.get("state") as SectionState)
    : null;

  // --- tab switching ------------------------------------------------------
  //
  // `replace: true` matches My Books and keeps Back out of a four-entry tab
  // walk — on a discovery surface, Back should leave the library. Tab-scoped
  // params are dropped so switching away from Browse all doesn't carry a stale
  // `kind=` into Collections.
  const setTab = useCallback(
    (next: LibraryTab) => {
      const nextParams = new URLSearchParams(params);
      for (const key of TAB_SCOPED_PARAMS) nextParams.delete(key);
      if (next === DEFAULT_TAB) nextParams.delete("tab");
      else nextParams.set("tab", next);
      setParams(nextParams, { replace: true });
    },
    [params, setParams],
  );

  // `ScrollToTop` ignores search-only changes, so scroll back up on a TAB
  // change — but not on a filter change, where holding position is correct.
  const lastTab = useRef(tab);
  useEffect(() => {
    if (lastTab.current === tab) return;
    lastTab.current = tab;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
  }, [tab]);

  // Warm a sibling tab's chunk on hover/focus, the way `lib/prefetch.ts` warms
  // book payloads. Fire-and-forget; a failure just means a Suspense fallback.
  const warmed = useRef(new Set<LibraryTab>());
  const prefetch = useCallback((next: LibraryTab) => {
    if (warmed.current.has(next)) return;
    warmed.current.add(next);
    const load = {
      reading: () => import("./library/ReadingNowTab"),
      store: () => import("./library/BookStoreTab"),
      browse: () => import("./library/BrowseAllTab"),
      collections: () => import("./library/CollectionsTab"),
    }[next];
    void load?.().catch(() => {});
  }, []);

  // --- Browse-all filter state, held in the URL --------------------------
  const filters: BrowseFilterState = useMemo(
    () => ({
      kind: params.get("kind"),
      lengths: (params.get("length") ?? "")
        .split(",")
        .filter((band): band is LengthBand => BANDS.includes(band as LengthBand)),
      illustrated: readIllustrated(params.get("plates")),
      sort: SORTS.includes(params.get("sort") as BrowseSort)
        ? (params.get("sort") as BrowseSort)
        : "newest",
    }),
    [params],
  );

  const setFilters = useCallback(
    (next: BrowseFilterState) => {
      const nextParams = new URLSearchParams(params);
      setOrDelete(nextParams, "kind", next.kind);
      setOrDelete(nextParams, "length", next.lengths.join(",") || null);
      // Omitted when both boxes are ticked — that's the same set as no filter.
      setOrDelete(
        nextParams,
        "plates",
        next.illustrated.length === 1 ? (next.illustrated[0] === "plates" ? "yes" : "no") : null,
      );
      setOrDelete(nextParams, "sort", next.sort === "newest" ? null : next.sort);
      // A new filter set is a new result set — page 3 of the old one is meaningless,
      // and keeping it strands the reader past the end of a shorter list.
      nextParams.delete("page");
      setParams(nextParams, { replace: true });
    },
    [params, setParams],
  );

  // --- search -------------------------------------------------------------
  //
  // The committed query lives in the URL beside the filters, so a search is
  // linkable and back-buttonable exactly like a facet is. Committing one also
  // moves the reader to Browse all: that tab already owns the facets, the sort
  // and the pager, and results deserve all three rather than a fifth surface
  // that has none of them.
  const setQuery = useCallback(
    (next: string) => {
      const nextParams = new URLSearchParams(params);
      setOrDelete(nextParams, "q", next.trim() || null);
      nextParams.delete("page");
      if (next.trim()) nextParams.set("tab", "browse");
      setParams(nextParams, { replace: true });
    },
    [params, setParams],
  );

  const setPage = useCallback(
    (next: number) => {
      const nextParams = new URLSearchParams(params);
      setOrDelete(nextParams, "page", next > 1 ? String(next) : null);
      setParams(nextParams, { replace: true });
      // A page change is a new screenful of covers. Holding scroll position would
      // land the reader mid-grid with no way to tell the page had turned.
      const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
    },
    [params, setParams],
  );

  // Typeahead. Kept OUT of `useAsync`: it is keyed by an uncommitted draft rather
  // than by the URL, and it must not blank or re-render the results underneath.
  const [draft, setDraft] = useState("");
  const [suggestions, setSuggestions] = useState<{ items: ShelfBook[]; total: number }>({
    items: [],
    total: 0,
  });
  const [suggesting, setSuggesting] = useState(false);
  const suggestGeneration = useRef(0);

  useEffect(() => {
    const term = draft.trim();
    if (!term) {
      setSuggestions({ items: [], total: 0 });
      setSuggesting(false);
      return;
    }
    setSuggesting(true);
    const mine = ++suggestGeneration.current;
    const timer = window.setTimeout(() => {
      fetchBrowse({
        kind: null,
        lengths: [],
        illustrated: [],
        sort: "newest",
        q: term,
        limit: SUGGEST_LIMIT,
      })
        .then((payload) => {
          // A generation counter, not an AbortController: responses can land out of
          // order, and the last one the reader ASKED for is the one that must win.
          if (suggestGeneration.current !== mine) return;
          setSuggestions({ items: payload.items, total: payload.total });
          setSuggesting(false);
        })
        .catch(() => {
          if (suggestGeneration.current !== mine) return;
          // A failed suggestion is not worth an error state: the field still
          // submits, and the results page reports its own failure honestly.
          setSuggestions({ items: [], total: 0 });
          setSuggesting(false);
        });
    }, SUGGEST_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft]);

  // --- data ---------------------------------------------------------------
  //
  // Each tab fetches only when it is the active one, so opening /library does not
  // pull all four payloads. Chart sort is derived from the store payload's own
  // `available_metrics`, never assumed.
  const chartSort: "newest" | "longest" = filters.sort === "longest" ? "longest" : "newest";

  const readingNow = useAsync(fetchReadingNow, [], { enabled: tab === "reading" });
  const store = useAsync(() => fetchStore(chartSort), [chartSort], { enabled: tab === "store" });
  const browse = useAsync(
    () =>
      fetchBrowse({
        kind: filters.kind,
        lengths: filters.lengths,
        illustrated: filters.illustrated,
        sort: filters.sort,
        q: query,
        page,
        limit: PAGE_SIZE,
      }),
    [
      filters.kind,
      filters.lengths.join(","),
      filters.illustrated.join(","),
      filters.sort,
      query,
      page,
    ],
    { enabled: tab === "browse" },
  );
  // Wait for the auth probe to settle before asking: firing this while `ready` is
  // false would 401 for a signed-in reader whose token hasn't loaded yet.
  const myShelf = useAsync(fetchMyShelf, [], { enabled: authReady && signedIn });

  // The tab bar's meta line needs totals from whichever payload is loaded.
  const totals =
    readingNow.data?.totals ?? store.data?.totals ?? browse.data?.totals ?? EMPTY_TOTALS;

  /** The review override wins so `?state=` is still usable; otherwise the real state. */
  const stateFor = (result: { state: SectionState }): SectionState =>
    reviewOverride ?? result.state;

  return (
    <Shell>
      <div className="container-wide">
        {/* The positioning anchor is this INNER block, not `.container-wide` itself.
            The container carries responsive padding (`px-5 sm:px-8 md:px-12`), and
            `right-0` on it would resolve against its padding box — putting the field
            hard against the window edge instead of on the content edge. */}
        <div className="relative">
          <LibraryTabBar
            active={tab}
            onChange={setTab}
            meta={tabMeta(tab, totals)}
            onIntent={prefetch}
          />
          {/* At `lg` the field sits ON the tab row, flush right and optically centred
              against the pill group — that group starts at the bar's own `pt-9` (36px)
              and stands 57px tall, so a 41px field centres at 44px. Below `lg` it
              cannot share the row: the pills become a full-width horizontal scroller,
              so the field stacks under them at full width. */}
          <div className="mt-4 lg:absolute lg:right-0 lg:top-[44px] lg:mt-0 lg:w-[236px]">
            <ShelfSearchField
              value={query}
              suggestions={suggestions.items}
              suggestionTotal={suggestions.total}
              suggesting={suggesting}
              onDraft={setDraft}
              onSubmit={setQuery}
              onOpenBook={(book) => navigate(bookHref(book))}
            />
          </div>
        </div>
      </div>

      <div className="container-wide">
        {LIBRARY_TABS.map((key) => (
          <div
            key={key}
            role="tabpanel"
            id={`lib-panel-${key}`}
            aria-labelledby={`lib-tab-${key}`}
            tabIndex={-1}
            hidden={key !== tab}
          >
            {key === tab && (
              <Suspense fallback={<TabFallback />}>
                {key === "reading" && (
                  <ReadingNowTab
                    data={{
                      hero: readingNow.data?.hero ?? null,
                      rails: readingNow.data?.rails ?? [],
                      genres: readingNow.data?.genres ?? [],
                      totals,
                      // Merged in from the separate per-reader call. Anonymous visitors
                      // correctly get none, which the tab renders as a sign-in prompt.
                      continue_reading: myShelf.data?.continue_reading ?? [],
                    }}
                    state={stateFor(readingNow)}
                    signedIn={signedIn}
                  />
                )}
                {key === "store" && store.data && (
                  <BookStoreTab data={store.data} state={stateFor(store)} />
                )}
                {key === "store" && !store.data && <TabFallback />}
                {key === "browse" && (
                  <BrowseAllTab
                    data={
                      browse.data ?? {
                        items: [],
                        total: 0,
                        next_cursor: null,
                        applied_sort: filters.sort,
                        facets: {
                          genres: [],
                          lengths: { "under-100": 0, "100-250": 0, "over-250": 0 },
                          plates: { with_plates: 0, text_only: 0 },
                        },
                        totals,
                      }
                    }
                    state={stateFor(browse)}
                    filters={filters}
                    onFilters={setFilters}
                    query={query}
                    onClearQuery={() => setQuery("")}
                    page={page}
                    pageSize={PAGE_SIZE}
                    onPage={setPage}
                  />
                )}
                {key === "collections" && (
                  <CollectionsTab
                    data={{ collections: [], featured_slug: "", totals }}
                    state={reviewOverride ?? (totals.collections === 0 ? "empty" : "loading")}
                    featuredSlug={params.get("c")}
                  />
                )}
              </Suspense>
            )}
          </div>
        ))}
      </div>
    </Shell>
  );
}

/** Shown only for the moment a tab's chunk is in flight. */
function TabFallback() {
  return (
    <div className="mt-9 pb-16" aria-busy="true">
      <span className="sr-only">Loading…</span>
      <BlockSkeleton className="h-[380px]" />
      <RailSkeleton />
    </div>
  );
}

function readIllustrated(value: string | null): IllustratedFilter[] {
  if (value === "yes") return ["plates"];
  if (value === "no") return ["text-only"];
  return [];
}

function setOrDelete(params: URLSearchParams, key: string, value: string | null) {
  if (value) params.set(key, value);
  else params.delete(key);
}
